const roleDefinitions = Object.freeze([
  Object.freeze({ value: 1, code: "OrganizationAdministrator", label: "Organization Administrator" }),
  Object.freeze({ value: 2, code: "ContentManager", label: "Content Manager" }),
  Object.freeze({ value: 3, code: "Viewer", label: "Viewer" }),
]);

const invitationStatuses = new Map([
  ["pending", "Pending"],
  ["accepted", "Accepted"],
  ["revoked", "Revoked"],
  ["expired", "Expired"],
]);

export const organizationRoleOptions = roleDefinitions;

export function organizationRoleValue(value) {
  const numeric = typeof value === "number" ? value : /^\d+$/.test(String(value ?? "")) ? Number(value) : null;
  const text = String(value ?? "").replace(/\s+/g, "").toLowerCase();
  const match = roleDefinitions.find(role => role.value === numeric || role.code.toLowerCase() === text);
  if (!match) throw new Error("Select Organization Administrator, Content Manager, or Viewer.");
  return match.value;
}

export function organizationRoleLabel(value) {
  const numeric = organizationRoleValue(value);
  return roleDefinitions.find(role => role.value === numeric).label;
}

export function normalizeOrganizationMember(value) {
  if (!value || typeof value !== "object") throw new Error("Pirdy returned an invalid organization member.");
  const userId = bounded(value.userId, 64, "Organization member identity", true);
  if (typeof value.enabled !== "boolean") throw new Error("Pirdy returned an invalid organization member state.");
  return Object.freeze({
    userId,
    email: email(value.email, false),
    phone: bounded(value.phone, 32, "Phone", false),
    displayName: bounded(value.displayName, 256, "Display name", false),
    role: organizationRoleValue(value.role),
    enabled: value.enabled,
  });
}

export function organizationMemberUpdate(member, changes, enabled = member?.enabled) {
  const current = normalizeOrganizationMember(member);
  if (typeof enabled !== "boolean") throw new Error("Choose whether this organization member is active.");
  return Object.freeze({
    userId: current.userId,
    email: email(changes?.email, false),
    phone: bounded(changes?.phone, 32, "Phone", false),
    displayName: bounded(changes?.displayName, 256, "Display name", false),
    role: organizationRoleValue(changes?.role),
    enabled,
  });
}

export function organizationInvitationRequest(value) {
  return Object.freeze({
    email: email(value?.email, true),
    role: organizationRoleValue(value?.role),
  });
}

export function normalizeOrganizationInvitation(value) {
  if (!value || typeof value !== "object") throw new Error("Pirdy returned an invalid organization invitation.");
  const invitationId = bounded(value.invitationId, 64, "Invitation code", true);
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(invitationId))
    throw new Error("Pirdy returned an invalid organization invitation code.");
  const status = invitationStatuses.get(String(value.status ?? "").trim().toLowerCase());
  if (!status) throw new Error("Pirdy returned an invalid organization invitation state.");
  const createdAtUtc = utcInstant(value.createdAtUtc, "Invitation creation time");
  const expiresAtUtc = utcInstant(value.expiresAtUtc, "Invitation expiry time");
  if (expiresAtUtc <= createdAtUtc) throw new Error("Pirdy returned an invalid organization invitation period.");
  return Object.freeze({
    invitationId: invitationId.toLowerCase(),
    email: email(value.email, true),
    role: organizationRoleValue(value.role),
    status,
    createdAtUtc: createdAtUtc.toISOString(),
    expiresAtUtc: expiresAtUtc.toISOString(),
    acceptedAtUtc: optionalUtcInstant(value.acceptedAtUtc, "Invitation acceptance time"),
    revokedAtUtc: optionalUtcInstant(value.revokedAtUtc, "Invitation revocation time"),
  });
}

export function confirmOrganizationMemberUpdate(response, expected) {
  const confirmed = normalizeOrganizationMember(response);
  const requested = organizationMemberUpdate(expected, expected, expected.enabled);
  if (confirmed.userId !== requested.userId || confirmed.role !== requested.role || confirmed.enabled !== requested.enabled ||
      confirmed.email !== requested.email || confirmed.phone !== requested.phone || confirmed.displayName !== requested.displayName)
    throw new Error("Pirdy did not confirm the requested organization member changes. Refresh before trying again.");
  return confirmed;
}

export function confirmOrganizationInvitation(response, { request = null, invitationId = null, status = null } = {}) {
  const confirmed = normalizeOrganizationInvitation(response);
  if (request) {
    const expected = organizationInvitationRequest(request);
    if (confirmed.email.toLowerCase() !== expected.email.toLowerCase() || confirmed.role !== expected.role || confirmed.status !== "Pending")
      throw new Error("Pirdy did not confirm the requested invitation. Refresh before trying again.");
  }
  if (invitationId && confirmed.invitationId !== String(invitationId).toLowerCase())
    throw new Error("Pirdy did not confirm the selected invitation. Refresh before trying again.");
  if (status && confirmed.status !== status)
    throw new Error("Pirdy did not confirm the requested invitation state. Refresh before trying again.");
  return confirmed;
}

function bounded(value, maximum, label, required) {
  if (value == null || String(value).trim() === "") {
    if (required) throw new Error(`${label} is required.`);
    return null;
  }
  const normalized = String(value).trim();
  if (normalized.length > maximum || /[\u0000-\u001f\u007f]/.test(normalized))
    throw new Error(`${label} must use no more than ${maximum} printable characters.`);
  return normalized;
}

function email(value, required) {
  const normalized = bounded(value, 320, "Email", required);
  if (normalized === null) return null;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) throw new Error("Enter a valid email address.");
  return normalized.toLowerCase();
}

function utcInstant(value, label) {
  if (typeof value !== "string" || !/(?:Z|[+-]00:00)$/i.test(value)) throw new Error(`${label} must be UTC.`);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) throw new Error(`${label} is invalid.`);
  return parsed;
}

function optionalUtcInstant(value, label) {
  return value == null ? null : utcInstant(value, label).toISOString();
}
